import { useMemo } from 'react';

import { LoadingWheel } from '../base/LoadingWheel';
import { IAppliance } from '../../types/Appliance/Appliance';
import { InputType } from '../../types/Action/ActionProfile';
import { Modal, ModalControlProps } from '../../layouts/Modal';
import useActionsByAppliance from '../../data/action/useActionsByAppliance';
import { Button } from '../base/Button';
import { IAction } from '../../types/Action/Action';
import useApiHelper from '../../hooks/useApiHelper';
import { useNotification } from '../../hooks/useNotifications';

export interface ApplianceFirmwareUpdateProps extends ModalControlProps {
  appliance: IAppliance;
}

export function ApplianceFirmwareUpdate({
  open,
  onClose,
  appliance,
}: ApplianceFirmwareUpdateProps) {
  const addNotification = useNotification();
  const { post } = useApiHelper();
  const { actions } = useActionsByAppliance(appliance.id);

  const firmwareUpdateActions = useMemo(
    () =>
      actions.isLoading
        ? []
        : actions.data?.filter((a) => a.inputType === InputType.Update),
    [actions.isLoading, actions.data]
  );

  const handleSend = (action: IAction) => {
    post(`/v1/action/${appliance.id}`, {
      value: null,
      action: action.id,
    })
      .then(() => {
        addNotification('success', 'Firmware update initiated successfully.');
      })
      .catch((e) => {
        addNotification('error', e.response.data.message);
      });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Firmware Update Appliance"
      description="Trigger a firmware update for the appliance."
    >
      {actions.isLoading ? (
        <LoadingWheel />
      ) : (
        <div className="mt-6">
          <table className="min-w-full table border border-primary/40 divide-y divide-primary dark:border-primary">
            <thead className="bg-primary/40">
              <tr>
                <th className="px-3 py-3 text-left text-sm font-semibold">
                  Name
                </th>
                <th className="px-3 py-3 text-left text-sm font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary/40">
              {firmwareUpdateActions!.map((action, index) => (
                <tr
                  key={action.id}
                  className={index % 2 !== 0 ? 'bg-primary/10' : ''}
                >
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    {action.name}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <Button onClick={() => handleSend(action)}>Send</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
